import { sanitizePostHtml } from './jotformEmbed';

const GENERIC_ALT = new Set([
  'image',
  'img',
  'photo',
  'picture',
  'logo',
  'icon',
  'banner',
  'thumbnail',
  'graphic',
  'spacer',
  'untitled',
]);

function isGenericAlt(alt) {
  if (!alt || typeof alt !== 'string') return true;
  const normalized = alt.trim().toLowerCase();
  if (!normalized) return true;
  return GENERIC_ALT.has(normalized) || /^image\s*\d*$/i.test(normalized);
}

function extractFilenameAlt(src) {
  if (!src) return '';
  try {
    const path = src.split('?')[0].split('#')[0];
    const name = path.split('/').pop() || '';
    const base = decodeURIComponent(name).replace(/\.[a-z0-9]+$/i, '');
    const words = base.replace(/[-_]+/g, ' ').trim();
    if (words.length >= 2) {
      return words.charAt(0).toUpperCase() + words.slice(1);
    }
  } catch {
    /* ignore */
  }
  return '';
}

function isLikelyDecorative(img) {
  const alt = img.getAttribute('alt');
  if (alt === '') return true;
  const role = (img.getAttribute('role') || '').toLowerCase();
  if (role === 'presentation' || role === 'none') return true;
  const cls = (img.getAttribute('class') || '').toLowerCase();
  if (cls.includes('decorative') || cls.includes('spacer')) return true;
  const w = parseInt(img.getAttribute('width') || '', 10);
  const h = parseInt(img.getAttribute('height') || '', 10);
  if ((w > 0 && w <= 2) || (h > 0 && h <= 2)) return true;
  return false;
}

function markDecorative(img) {
  img.setAttribute('alt', '');
  img.setAttribute('role', 'presentation');
  img.setAttribute('aria-hidden', 'true');
}

function ensureTableHeaders(table) {
  if (table.querySelector('th')) {
    table.querySelectorAll('th').forEach((th) => {
      if (!th.getAttribute('scope')) {
        const inThead = th.closest('thead');
        th.setAttribute('scope', inThead ? 'col' : 'col');
      }
    });
    return;
  }

  const firstRow = table.querySelector('tr');
  if (!firstRow) return;

  const cells = firstRow.querySelectorAll('td');
  if (cells.length < 2) return;

  let thead = table.querySelector('thead');
  if (!thead) {
    thead = table.ownerDocument.createElement('thead');
    table.insertBefore(thead, table.firstChild);
  }

  const headerRow = table.ownerDocument.createElement('tr');
  cells.forEach((td) => {
    const th = table.ownerDocument.createElement('th');
    th.setAttribute('scope', 'col');
    th.innerHTML = td.innerHTML;
    headerRow.appendChild(th);
    td.remove();
  });
  thead.appendChild(headerRow);
}

/**
 * Activate clickable card/row controls from keyboard (Enter or Space).
 */
export function handleActionKeyDown(event, action) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
}

/**
 * Improves CMS/HTML fragments for WCAG 1.1.1, 1.3.1, and 4.1.2 before render.
 */
export function enhanceAccessibleHtml(html, options = {}) {
  if (!html || typeof html !== 'string') return html;
  if (typeof DOMParser === 'undefined') return html;

  const { pageTitle = '', defaultImageAlt = 'Illustration' } = options;
  const sanitized = sanitizePostHtml(html);

  try {
    const doc = new DOMParser().parseFromString(sanitized, 'text/html');

    doc.querySelectorAll('img').forEach((img) => {
      if (isLikelyDecorative(img)) {
        markDecorative(img);
        return;
      }

      const alt = img.getAttribute('alt');
      if (alt && !isGenericAlt(alt)) return;

      const figcaption = img.closest('figure')?.querySelector('figcaption')?.textContent?.trim();
      const titleAttr = img.getAttribute('title')?.trim();
      const src = img.getAttribute('src') || '';
      const derived =
        figcaption ||
        (titleAttr && !isGenericAlt(titleAttr) ? titleAttr : '') ||
        extractFilenameAlt(src) ||
        pageTitle ||
        defaultImageAlt;

      img.setAttribute('alt', derived);
      img.removeAttribute('aria-hidden');
      img.removeAttribute('role');
    });

    doc.querySelectorAll('table').forEach(ensureTableHeaders);

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

/**
 * Removes focusable/interactive descendants from HTML fragments rendered inside
 * a single card button (WCAG 4.1.2 — no nested interactive controls).
 */
export function neutralizeInteractiveHtml(html) {
  if (!html || typeof html !== 'string') return html;
  if (typeof DOMParser === 'undefined') return html;

  const sanitized = sanitizePostHtml(html);

  try {
    const doc = new DOMParser().parseFromString(sanitized, 'text/html');

    doc.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
      if (el.tagName.toLowerCase() === 'a') {
        const span = doc.createElement('span');
        span.innerHTML = el.innerHTML;
        el.replaceWith(span);
        return;
      }
      el.removeAttribute('tabindex');
      if (el.hasAttribute('href')) el.removeAttribute('href');
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

export default enhanceAccessibleHtml;
